/**
 * The progress bar on the boot sheet — a deliberate deviation, at the user's
 * request (2026-09-08).
 *
 * ⚠️ **The original has nothing here, and that is measured, not assumed.**
 * `Bootstrapper.unity` is a SolidColor camera and an *empty* Canvas
 * (`m_Children: []`), and `Bootstrapper.<Awake>d__10.MoveNext` reaches
 * `SceneManager.LoadSceneAsync(index, Single)` without ever touching
 * `PopupService` — so the first load has no popup, no iris and no meter. It
 * does not need one: the scene it swaps to is already on the device.
 * `bootCover.test.ts` still holds that claim to the scene file and the decomp,
 * and the sheet keeps the camera's colour (`#301867`) and its hard cut.
 *
 * The web build differs in the one way that matters: it fetches ~9.1 MB before
 * the first level can stand, and a purple sheet with no feedback is
 * indistinguishable from a page that has hung. So the sheet gains a bar.
 *
 * ❓ PLACEHOLDER — **every pixel of the bar is invented.** Nothing in the kit
 * describes a loading meter: `Sprite/` has `NewFeaturesProgress`, but that is
 * the success panel's feature meter (see `popups.ts`), and reusing it here
 * would be dressing an invention as a port. It is drawn in CSS instead, in the
 * sheet's own ink, so no one reads it as recovered art. To close this, art
 * from the publisher would be needed — the kit cannot.
 *
 * The DOM is built here rather than shipped in `index.html` because the sheet
 * itself must paint before any script runs, while the bar has nothing to
 * report until the module that does the fetching is alive.
 */

/**
 * The four things the boot waits on, in the order it waits on them.
 *
 * `manifest` is `AssetLibrary.load`, `meshes` is `loadMeshes`, `textures` is
 * `loadTextures`, and `scene` is the level fetch plus `App.build`.
 */
export type BootStage = 'manifest' | 'meshes' | 'textures' | 'scene'

/**
 * Where each stage's slice of the bar starts and ends.
 *
 * 📐 The three fetch stages are weighted by the bytes they actually await:
 * `assets/manifest.json` is 320 KB, the mesh binaries are 3.27 MB and the
 * textures 5.46 MB — 3.5 %, 36.1 % and 60.4 % of the payload.
 * `bootLoader.test.ts` reads all three off disk and holds these numbers to
 * them, so a pipeline change that moves the ratio turns the test red instead of
 * leaving the bar quietly lying.
 *
 * ⚠️ `textures` was not on this list until 2026-09-08, and its absence was
 * visible: `AssetLibrary.texture` is fire-and-forget, so the sheet lifted with
 * the *larger* half of the payload still in flight and the first second of the
 * page was the level in pale grey. See `AssetLibrary.loadTextures`.
 *
 * ❓ `scene` is a **tail**, not a measurement: the level JSON is a few KB and
 * `App.build` awaits no bytes at all, so there is nothing to count. The bar
 * holds at 94 % while the scene is built rather than sitting at 100 % over a
 * screen that is not there yet.
 */
export const BOOT_SEGMENTS: Readonly<Record<BootStage, readonly [number, number]>> = {
  manifest: [0, 0.033],
  meshes: [0.033, 0.373],
  textures: [0.373, 0.94],
  scene: [0.94, 1],
}

/**
 * Where the bar stands, in 0..1, when `done` of `total` steps of `stage` are
 * finished.
 *
 * `total <= 0` means "the stage has no countable steps" and reads as its start,
 * not as a division by zero.
 */
export function bootProgress(stage: BootStage, done = 1, total = 1): number {
  const [start, end] = BOOT_SEGMENTS[stage]
  if (total <= 0) return start
  const share = Math.min(1, Math.max(0, done / total))
  return start + (end - start) * share
}

/**
 * The name over the bar.
 *
 * 📐 `ProjectSettings/ProjectSettings.asset:20` — `productName: Bus Traffic
 * Loop`. Typed out rather than styled from art because the build ships no
 * wordmark: `Sprite/` has no logo, and `Splash_Vignate`, the only candidate, is
 * an orphan nothing references (`bootCover.test.ts` proves it).
 */
export const GAME_NAME = 'Bus Traffic Loop'

export class BootLoader {
  private readonly fill: HTMLElement | null = null
  /**
   * The bar never walks backwards. Stages are reported in order, but a
   * `loadMeshes` callback can land after the next stage has been announced
   * (the promises resolve in whatever order the network gives them), and a
   * meter that jumps back reads as a fault.
   */
  private progress = 0

  /** `cover` is `#bootCover`, or null on a page that has already dropped it. */
  constructor(cover: HTMLElement | null) {
    if (!cover) return
    const bar = document.createElement('div')
    bar.className = 'bootBar'
    const title = document.createElement('div')
    title.className = 'bootTitle'
    title.textContent = GAME_NAME
    const track = document.createElement('div')
    track.className = 'bootTrack'
    this.fill = document.createElement('div')
    this.fill.className = 'bootFill'
    track.append(this.fill)
    bar.append(title, track)
    cover.append(bar)
    this.paint()
  }

  set(stage: BootStage, done = 1, total = 1): void {
    const next = bootProgress(stage, done, total)
    if (next <= this.progress) return
    this.progress = next
    this.paint()
  }

  private paint(): void {
    if (!this.fill) return
    this.fill.style.width = `${this.progress * 100}%`
  }
}
